import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav 
      className="nav-bar-micro1"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        padding: '18px 0',
        transition: 'background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
        backgroundColor: scrolled ? 'rgba(18, 18, 18, 0.85)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid transparent',
      }}
    >
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        
        {/* Left Navigation Links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }} className="nav-left-desktop">
          <Link 
            to="/" 
            style={{ 
              fontSize: '13px', 
              color: isActive('/') ? '#FFFFFF' : '#D7D7D7', 
              transition: 'color 0.15s ease',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Overview
          </Link>
          <a 
            href="/#architecture" 
            style={{ 
              fontSize: '13px', 
              color: '#D7D7D7', 
              transition: 'color 0.15s ease',
              fontWeight: 400 
            }}
          >
            Architecture
          </a>
          <Link 
            to="/compare" 
            style={{ 
              fontSize: '13px', 
              color: isActive('/compare') ? '#FFFFFF' : '#D7D7D7', 
              transition: 'color 0.15s ease',
              fontWeight: 400 
            }}
          >
            Why Draway
          </Link>
          <Link 
            to="/pricing" 
            style={{ 
              fontSize: '13px', 
              color: isActive('/pricing') ? '#FFFFFF' : '#D7D7D7', 
              transition: 'color 0.15s ease',
              fontWeight: 400 
            }}
          >
            Pricing
          </Link>
          <Link 
            to="/docs" 
            style={{ 
              fontSize: '13px', 
              color: isActive('/docs') ? '#FFFFFF' : '#D7D7D7', 
              transition: 'color 0.15s ease',
              fontWeight: 400 
            }}
          >
            Docs
          </Link>
        </div>

        {/* Center Logo: clean geometric wordmark like micro1. */}
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'baseline', textDecoration: 'none' }}>
            <span style={{ 
              fontFamily: "'Outfit', sans-serif", 
              fontSize: '22px', 
              fontWeight: 600, 
              color: '#FFFFFF',
              letterSpacing: '-0.5px'
            }}>
              draway<span style={{ color: '#6D7CFF' }}>.</span>
            </span>
          </Link>
        </div>

        {/* Right CTA Button: micro1 pill shape with subtle arrow icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link 
            to="/waitlist" 
            className="micro1-pill-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#FFFFFF',
              color: '#121212',
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '13px',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'transform 0.15s ease, background-color 0.15s ease',
            }}
          >
            <span>Get in touch</span>
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#121212',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ChevronRight size={12} strokeWidth={2.5} />
            </div>
          </Link>

          {/* Mobile Hamburger Toggle */}
          <button 
            type="button" 
            className="mobile-toggle-btn"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle navigation"
            style={{ color: '#FFFFFF', display: 'none' }}
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

      </div>

      {/* Mobile Drawer */}
      {isOpen && (
        <div style={{
          backgroundColor: '#121212',
          borderBottom: '1px solid #374151',
          padding: '24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginTop: '12px'
        }}>
          <Link to="/" onClick={() => setIsOpen(false)} style={{ color: '#FFFFFF', fontSize: '15px' }}>Overview</Link>
          <a href="/#architecture" onClick={() => setIsOpen(false)} style={{ color: '#D7D7D7', fontSize: '15px' }}>Architecture</a>
          <Link to="/compare" onClick={() => setIsOpen(false)} style={{ color: '#D7D7D7', fontSize: '15px' }}>Why Draway</Link>
          <Link to="/pricing" onClick={() => setIsOpen(false)} style={{ color: '#D7D7D7', fontSize: '15px' }}>Pricing</Link>
          <Link to="/docs" onClick={() => setIsOpen(false)} style={{ color: '#D7D7D7', fontSize: '15px' }}>Documentation</Link>
          <Link to="/security" onClick={() => setIsOpen(false)} style={{ color: '#D7D7D7', fontSize: '15px' }}>Security &amp; Vault</Link>
          <Link 
            to="/waitlist" 
            onClick={() => setIsOpen(false)} 
            style={{ 
              backgroundColor: '#FFFFFF', 
              color: '#121212', 
              padding: '10px 16px', 
              borderRadius: '9999px', 
              textAlign: 'center', 
              fontWeight: 500,
              fontSize: '14px',
              marginTop: '8px'
            }}
          >
            Get in touch &rarr;
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
